import { useCallback, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const useNotification = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  /**
   * Triggers a snackbar notification.
   *
   * @param message - The message to display in the snackbar.
   * @param variant - The type of notification ('default', 'success', 'error', 'warning', 'info').
   * @param options - Additional options for customizing the snackbar.
   */
  const notify = useCallback((
    message: string,
    variant: 'default' | 'success' | 'error' | 'warning' | 'info' = 'default',
    options: any = {}
  ) => {
    const isPersistent = variant === 'error' || variant === 'warning';
    // Returns the snackbar key so a caller that fires notifications from a
    // live search (e.g. "no results for this query") can dismiss its own
    // previous toast before showing an updated one, instead of stacking
    // persistent error toasts as the user keeps typing.
    return enqueueSnackbar(message, {
      variant,
      persist: isPersistent,
      autoHideDuration: isPersistent ? null : 5000,
      action: (key) => (
        <IconButton
          size="small"
          aria-label="close"
          color="inherit"
          onClick={() => closeSnackbar(key)}
          style={{ padding: '2px' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      ),
      ...options,
    });
  }, [enqueueSnackbar, closeSnackbar]);

  /**
   * Displays a full-width banner notification at the top of the page.
   *
   * @param message - The message to display in the banner.
   * @param variant - The type of notification ('default', 'success', 'error', 'warning', 'info').
   * @param options - Additional options for customizing the banner's appearance.
   */
  const showBanner = useCallback((
    message: string,
    variant: 'default' | 'success' | 'error' | 'warning' | 'info' = 'default',
    options: any = {}
  ) => {
    const isPersistent = variant === 'error' || variant === 'warning';
    enqueueSnackbar(message, {
      variant,
      persist: isPersistent,
      autoHideDuration: isPersistent ? null : 6000,
      anchorOrigin: {
        vertical: 'top',
        horizontal: 'right'
      },
      action: (key) => (
        <IconButton
          size="small"
          aria-label="close"
          color="inherit"
          onClick={() => closeSnackbar(key)}
          style={{ padding: '2px' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      ),
      ...options,
    });
  }, [enqueueSnackbar, closeSnackbar]);

  /**
   * Dismisses all or a specific snackbar.
   *
   * @param key - The key of the snackbar to close (optional).
   */
  const dismiss = useCallback((key?: string | number) => {
    closeSnackbar(key);
  }, [closeSnackbar]);

  return useMemo(
    () => ({ notify, dismiss, showBanner }),
    [notify, dismiss, showBanner]
  );
};

export default useNotification;
